import { WebPathDocumentTitle } from '@/core/components/WebDocumentHead';
import { preloadAmenityCatalog } from '@/core/services/amenityCatalogService';
import { preloadTranslations, loadAdhaTranslationsOnly } from '@/core/services/translationService';
import { useAppStore } from '@/core/stores/appStore';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { View, Platform } from 'react-native';
import { logger } from '../logger';
import { SupportedLanguage } from '../types/i18n';
import { syncWebDocumentDirection, viewLayoutDirection } from '../utils/layoutDirection';
import { initializeI18n, isRTLLanguage, setLanguage } from '../utils/i18n';

interface I18nContextType {
  currentLanguage: SupportedLanguage;
  isLoading: boolean;
  changeLanguage: (language: SupportedLanguage) => Promise<void>;
  isRTL: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const useI18nContext = () => {
  const context = useContext(I18nContext);
  if (!context) {
    // Graceful fallback during fast refresh or early renders before provider mount
    // Silent fallback - no warning needed as this is expected during initial render
    return {
      currentLanguage: 'en',
      isLoading: false,
      changeLanguage: async () => { },
      isRTL: false,
    } as I18nContextType;
  }
  return context;
};

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>('en');
  const [isLoading, setIsLoading] = useState(true);
  const [isRTL, setIsRTL] = useState(false);
  const isAdhaJourney = useAppStore((state) => state.isAdhaJourney);
  const isAuthenticated = useAppStore((state) => state.auth.isAuthenticated);

  useEffect(() => {
    if (!isAdhaJourney || isLoading) {
      return;
    }
    void loadAdhaTranslationsOnly(currentLanguage);
  }, [isAdhaJourney, currentLanguage, isLoading]);

  // Web: logged-in screens show amenities right away, so preload once a session
  // exists (restored on refresh or fresh login). Guests load it from useWebAmenities.
  useEffect(() => {
    if (Platform.OS !== 'web' || !isAuthenticated || isLoading) {
      return;
    }
    void preloadAmenityCatalog(currentLanguage);
  }, [isAuthenticated, currentLanguage, isLoading]);

  // Initialize i18n on mount and reload on fast refresh (development)
  useEffect(() => {
    const initialize = async () => {
      try {
        const language = await initializeI18n();
        // Always reload translations (handles fast refresh where memoryCache is cleared)
        await preloadTranslations(language as SupportedLanguage);
        // Web preloads from the auth effect above (logged in) or useWebAmenities (guest).
        if (Platform.OS !== 'web') {
          void preloadAmenityCatalog(language as SupportedLanguage);
        }
        setCurrentLanguage(language);
        setIsRTL(isRTLLanguage(language));
      } catch (error) {
        logger.error('I18nProvider', 'Failed to initialize i18n:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void initialize();

    // In development, reload translations on fast refresh
    if (__DEV__) {
      // @ts-ignore - Metro hot reload API
      if (module.hot) {
        // @ts-ignore
        module.hot.accept(() => {
          // Reload translations when module hot reloads (fast refresh)
          initialize().catch((e) => logger.warn('I18nProvider', 'Hot reload preload failed', e));
        });
      }
    }
  }, []);

  useEffect(() => {
    syncWebDocumentDirection(isRTL);
  }, [isRTL]);

  const changeLanguage = async (newLanguage: SupportedLanguage) => {
    try {
      logger.info('I18nProvider', 'Changing language', { from: currentLanguage, to: newLanguage });
      await setLanguage(newLanguage);
      if (Platform.OS !== 'web') {
        void preloadAmenityCatalog(newLanguage);
      }
      if (Platform.OS === 'web') {
        const preloadPromise = preloadTranslations(newLanguage);
        setCurrentLanguage(newLanguage);
        setIsRTL(isRTLLanguage(newLanguage));
        logger.info('I18nProvider', 'Language changed successfully', { newLanguage, isRTL: isRTLLanguage(newLanguage) });
        await preloadPromise;
      } else {
        await preloadTranslations(newLanguage);
        setCurrentLanguage(newLanguage);
        setIsRTL(isRTLLanguage(newLanguage));
        logger.info('I18nProvider', 'Language changed successfully', { newLanguage, isRTL: isRTLLanguage(newLanguage) });
      }
    } catch (error) {
      logger.error('I18nProvider', 'Failed to change language:', error);
      throw error;
    }
  };

  const value: I18nContextType = {
    currentLanguage,
    isLoading,
    changeLanguage,
    isRTL,
  };

  if (isLoading) {
    // Route screens are not mounted yet. Publish the document title now so the
    // initial HTML is not an empty <title> (search and link previews read it).
    if (Platform.OS === 'web') {
      return <WebPathDocumentTitle />;
    }
    return null;
  }

  const nativeLayoutDirection = isRTL ? 'rtl' : 'ltr';
  const nativeRootDirectionStyle =
    Platform.OS !== 'web' ? viewLayoutDirection(nativeLayoutDirection) : null;

  return (
    <I18nContext.Provider value={value}>
      <View style={[{ flex: 1 }, nativeRootDirectionStyle]}>
        {children}
      </View>
    </I18nContext.Provider>
  );
};